import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { SyncService } from 'src/app/services/sync.service';
import { FileUploadRoot } from 'src/model/Project/FileUploadRoot';
import { ProjectViewData } from 'src/model/Project/ProjectViewData';
import { environment } from 'src/environments/environment';
import { Project } from 'src/model/Project/Project';
import { configObj } from 'src/model/configObj';
import { ServerStatusResponse } from 'src/model/Project/ServerStatusResponse';
import { TranslateService } from '@ngx-translate/core';
import { clearAuthSession } from '../../auth/auth-session';

declare var config: any;
@Component({
  selector: 'app-sync',
  templateUrl: `./Sync.component.html`,
  styleUrls: ['./Sync.component.css'],
  animations: [
    trigger('fadeOut', [
      state('visible', style({ opacity: 1 })),
      state('hidden', style({ opacity: 0 })),
      transition('visible => hidden', animate('1000ms ease-out')),
    ]),
  ],
})
export class SyncComponent implements OnInit, OnDestroy {
  //init page Loading
  loading: boolean = true;
  fadeOut: boolean = false;
  //upload Status
  upload: number = 0; //控制  0:為上傳/1:上傳中/2:已上傳
  uploadPercentage: number = 0;
  uploadError: boolean = false;
  file: File = null;
  fileName: string | null = null;
  fileSize: number | null = null;
  uploadedSize: number | null = null;

  projectData: FileUploadRoot = new FileUploadRoot();

  id: any = null;
  detail: boolean = false;
  detailUUid: string = '';
  project: Project = new Project();

  codeUrl: string = null;
  visualUrl: string = null;

  //分頁
  pageSize: number = 5;
  pageIndex: number = 1;
  pageNumber: number = 1;
  config: configObj;
  appVersion: string = '';
  pageAry: number[] = [];
  min: number = 1;
  max: number = 1;

  uploadErrorMessage: string | null = null;
  uploadSuccessMessage: string | null = null;
  serverStatusMessage: string | null = null;
  serverStatusOk: boolean = true;

  constructor(private syncService: SyncService, private window: Window, private router: Router, private translate: TranslateService) { }

  get currentLang(): string {
    return this.translate.currentLang || this.translate.defaultLang || 'zh-TW';
  }

  ngOnInit(): void {
    this.fadeOut = true;
    setTimeout(() => {
      this.loading = false;
    }, 1000);
    this.codeUrl = environment.CodeUrl;
    this.visualUrl = environment.VisualUrl;
    this.config = this.window['config'] as configObj;
    this.pageSize = this.config.pageSize;
    this.appVersion = this.config.appVersion;
    this.queryProcess();

  }

  onFileSelected(event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.file = input.files[0];
      this.fileName = this.file.name;
      this.fileSize = this.file.size;
      this.upload = 1;
      this.uploadErrorMessage = null;
      this.uploadSuccessMessage = null;
      // Release the native selection so choosing the same corrected file fires change again.
      input.value = '';
      //this.uploadFile();
    }
  }

  uploadFile() {
    this.uploadPercentage = 0;
    this.upload = 0;
    this.syncService.uploadFile(this.file).subscribe(
      (resp) => {
        this.uploadErrorMessage = null;
        this.uploadSuccessMessage = null;

        if (resp.Progress === 100) {
          this.uploadPercentage = 100;
          this.uploadedSize = this.fileSize;
          this.upload = 2;
          this.uploadSuccessMessage = this.translate.instant('SYNC.UPLOAD_SUCCESS');
          this.uploadErrorMessage = null;
          //開始call進度
          this.queryProcess();
        } else if (resp.Progress >= 0 && resp.Obj?.status === 200) {
          // 上傳中（伺服器回 200）
          this.upload = 1;
          this.uploadPercentage = resp.Progress;
          this.uploadedSize = (this.fileSize * resp.Progress) / 100;
        } else if (resp.Progress < 0) {
          // 代表 service 已經把自訂錯誤回傳進來了
          this.upload = 2;
          this.uploadPercentage = 100;
          this.uploadedSize = this.fileSize;
          this.uploadSuccessMessage = null;
          this.uploadErrorMessage = resp.Obj?.detail || this.translate.instant('SYNC.UPLOAD_FAIL');
        }
      },
      (error) => {
        this.uploadError = true;
        this.upload = 2;
        this.uploadPercentage = 100;
        this.uploadedSize = this.fileSize;
        this.uploadErrorMessage = this.translate.instant('SYNC.NETWORK_ERROR');
      }
    );
  }

  codeResultUrl(project: Project): string | null {
    const patient = project.patients.find(p => p.serial != null && p.stages.some(a => a.medcat_num === 100));
    return patient ? `${this.codeUrl}/code/?CaseID=${encodeURIComponent(patient.serial)}&ProjectID=${project.uuid}&Editor=1&lang=${this.currentLang}` : null;
  }

  toDetail(project: Project) {
    this.detail = true;
    this.detailUUid = project.uuid;
    this.project = this.projectData.projects.filter(
      (s) => s.uuid == project.uuid
    )[0];
  }

  toHome() {
    this.detail = false;
    this.detailUUid = null;
    this.project = null;
  }
  ///查詢進度
  queryProcess() {
    if (this.id) clearInterval(this.id); //避免重複開interval
    this.getData();
    this.checkServer();
    this.id = setInterval(() => {
      this.getData();
      this.checkServer();
    }, 1000 * environment.Interval);
  }

  checkServer(): void {
    this.syncService.checkService().subscribe({
      next: (res: ServerStatusResponse) => {
        this.serverStatusOk = res.worker_online && res.queue_length !== null;
        this.serverStatusMessage = this.translate.instant(this.serverStatusOk ? 'SERVER_STATUS.OK' : 'SERVER_STATUS.ERROR', {
          count: res.queue_length, active: res.active_count, reserved: res.reserved_count, scheduled: res.scheduled_count
        });
      },
      error: (err) => {
        this.serverStatusOk = false;
        this.serverStatusMessage = this.translate.instant('SERVER_STATUS.ERROR');
      }
    });
  }

  getData() {
    this.syncService
      .getProjectData(this.pageIndex, this.pageSize)
      .subscribe((res) => {
        this.projectData = res;
        this.pageNumber = Math.ceil(res.total / this.config.pageSize);
        this.generatePaging();
        //計算成功率
        res.projects.forEach((t) => {
          try {
            let view: ProjectViewData[] = [];
            let sum = 0;
            let columns = 0;
            let success = 0;
            t.patients.forEach((s) => {
              s.stages.forEach((a) => {
                // sum += 6;
                sum += 4;
                columns++;

                if (t.upload_num == 100) success++;
                if (a.medcat_num == 100) success++;
                if (a.convert_num == 100) success++;
                // if (a.validate_num == 100 || a.validate_num < 0) success++;
                if (a.to_a_num == 100) success++;
                // if (a.to_b_num == 100 || a.to_b_num < 0) success++;

                let data = new ProjectViewData();
                // data.name = s.sqe + '-' + a.type_name;
                //data.name = s.serial + '-' + s.sqe + '-' + a.type_text;
                data.name = s.serial + '-' + a.type_text;
                data.type_name = a.type_name;
                data.type_num =
                  a.type_name == 'A' ? 1 : a.type_name == 'B' ? 2 : 3;
                data.sqe = +s.sqe;
                data.upload = t.upload_num;
                data.medcat_num = a.medcat_num;
                data.convert_num = a.convert_num;
                data.validate_num = a.validate_num;
                data.to_a_num = a.to_a_num;
                data.to_b_num = a.to_b_num;
                view.push(data);
              });
            });

            t.syncError = view.some(v => v.upload < 0 || v.medcat_num < 0 || v.convert_num < 0 || v.to_a_num < 0);
            t.syncComplete = false;
            t.codeResult = false;
            if (sum > 0 && success === sum && !t.syncError) {
              t.syncComplete = true;
              t.syncPercentage = 100;
            } else {
              t.syncPercentage = sum > 0 ? Math.round((success * 100) / sum) : 0;
            }
            //#region  計算title的status
            //upload_num
            if (view.filter((t) => t.upload < 0).length > 0) {
              //error優先
              t.upload_numAll = -1;
            } else if (view.filter((t) => t.upload == 0).length == columns) {
              //未執行/預設
              t.upload_numAll = 0;
            } else if (view.filter((t) => t.upload == 100).length == columns) {
              //未執行/預設
              t.upload_numAll = 100;
            } else {
              t.upload_numAll = 50; //執行中
            }

            //medcat_num
            if (view.filter((t) => t.medcat_num < 0).length > 0) {
              //error優先
              t.medcat_numAll = -1;
            } else if (
              view.filter((t) => t.medcat_num == 0).length == columns
            ) {
              //未執行/預設
              t.medcat_numAll = 0;
            } else if (
              view.filter((t) => t.medcat_num == 100).length == columns
            ) {
              //未執行/預設
              t.medcat_numAll = 100;
            } else {
              t.medcat_numAll = 50; //執行中
            }

            //convert
            if (view.filter((t) => t.convert_num == -1).length > 0) {
              //error優先
              t.convert_numAll = -1;
            } else if (
              view.filter((t) => t.convert_num == 0).length == columns
            ) {
              //未執行/預設
              t.convert_numAll = 0;
            } else if (
              view.filter((t) => t.convert_num == 100).length == columns
            ) {
              //未執行/預設
              t.convert_numAll = 100;
            } else {
              t.convert_numAll = 50; //執行中
            }

            //Vaildate
            if (view.filter((t) => t.validate_num == -1).length > 0) {
              //error優先
              t.validate_numAll = -1;
            } else if (
              view.filter((t) => t.validate_num == 0).length == columns
            ) {
              //未執行/預設
              t.validate_numAll = 0;
            } else if (
              view.filter((t) => t.validate_num == 100).length == columns
            ) {
              //整欄完成
              t.validate_numAll = 100;
            } else {
              t.validate_numAll = 50; //執行中
            }

            //to_a
            if (view.filter((t) => t.to_a_num == -1).length > 0) {
              //error優先
              t.to_a_numAll = -1;
            } else if (view.filter((t) => t.to_a_num == 0).length == columns) {
              //未執行/預設
              t.to_a_numAll = 0;
            } else if (
              view.filter((t) => t.to_a_num == 100).length == columns
            ) {
              //整欄完成
              t.to_a_numAll = 100;
            } else {
              t.to_a_numAll = 50; //執行中
            }

            //to_b
            if (view.filter((t) => t.to_b_num == -1).length > 0) {
              //error優先
              t.to_b_numAll = -1;
            } else if (view.filter((t) => t.to_b_num == 0).length == columns) {
              //未執行/預設
              t.to_b_numAll = 0;
            } else if (
              view.filter((t) => t.to_b_num == 100).length == columns
            ) {
              //整欄完成
              t.to_b_numAll = 100;
            } else {
              t.to_b_numAll = 50; //執行中
            }

            t.codeResult = t.patients.some(p => p.serial != null && p.stages.some(a => a.medcat_num === 100));
            const stages = t.patients.flatMap(p => p.stages);
            const failed = stages.filter(a => a.medcat_num < 0 || a.convert_num < 0 || a.to_a_num < 0).length;
            const completed = stages.filter(a => a.medcat_num === 100 && a.convert_num === 100 && a.to_a_num === 100).length;
            const pending = stages.length - failed - completed;
            t['processingSummary'] = this.translate.instant('SYNC.PROCESSING_SUMMARY', { completed, pending, failed });

            if (view.filter((t) => t.to_a_num == 100).length > 0)
              t.visualResult = true;

            t.projectView = view.sort(
              (a, b) => a.sqe * 10 + a.type_num - (b.sqe * 10 + b.type_num)
            );
          } catch {
            t.syncError = true;
          }
        });

        //內頁需指向新資料,否則畫面停在舊的 project 物件
        if (this.detail && this.detailUUid) {
          const current = res.projects.find((p) => p.uuid == this.detailUUid);
          if (current) this.project = current;
        }

        this.projectData.syncTime = new Date();
      });
    //#endregion
  }
  generatePaging() {
    // 原先的寫法
    // let min: number = 1;
    // let max: number = 1;
    // //預設是左2/右2
    // let left: number = 2;
    // let right: number = 2;
    // min = this.pageIndex - (this.pageIndex - 1 > 2 ? 2 : this.pageIndex - 1);
    // min = this.pageIndex - min < 1 ? 1 : this.pageIndex - min;
    // max = min + 4 > this.pageNumber ? this.pageNumber : min + 4;

    // this.pageIndex = 目前頁碼
    // this.pageNumber = 總頁碼 
    let totalPagesToShow = 5;   // 一次最多顯示多少頁碼index
    let half = Math.floor(totalPagesToShow / 2);
    let min = this.pageIndex - half;
    let max = this.pageIndex + half;

    // 修正邊界,如果min<1 往max進行補頁數,以便維持total顯示5頁index
    if (min < 1) {
      max += 1 - min;
      min = 1;
    }
    // 修正邊界,如果max>總頁碼 往min進行補頁數,以便維持total顯示5頁index
    if (max > this.pageNumber) {
      min -= max - this.pageNumber;
      max = this.pageNumber;
    }
    if (min < 1) min = 1; // 避免 min 負數
    this.pageAry = this.generateNumberArray(min, max);
    this.min = min;
    this.max = max;
  }

  generateNumberArray(min, max) {
    const arr = [];
    for (let i = min; i <= max; i++) {
      arr.push(i);
    }
    return arr;
  }

  gotoPage(page) {
    this.pageIndex = page;
    this.queryProcess(); //換頁後若有未完成專案需重新輪詢
  }

  reload() {
    window.location.reload();
  }

  ngOnDestroy(): void {
    if (this.id) clearInterval(this.id);
  }
  logout() {
    clearAuthSession();
    this.router.navigate(['/login']);
  }
}
